<template>
  <el-form
      @submit.prevent
      @keydown.enter.prevent
      novalidate
      :model="inventoryAdjustment"
      ref="inventoryAdjustmentForm"
      :rules="rules"
      label-position="top"
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-3/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('inventoryAdjustmentForm')"
          />
        </div>

        <div class="md:w-3/12 m-2">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('inventoryAdjustmentForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section class="my-2">
      <h1>{{ t('sections.info') }}</h1>
      <h2>{{ t('userAdjusting') }}: {{ user.fullName }}</h2>

      <el-form-item :label="t('reason')" class="input-label" prop="reason">
        <el-input
            id="reason"
            v-model="inventoryAdjustment.reason"
            :placeholder="t('placeholders.reason')"
            clearable
            required
        >
        </el-input>
      </el-form-item>

      <el-form-item class="input-label" style="margin: 5px auto">
        <el-checkbox
            id="add-accounting-transaction"
            v-model="inventoryAdjustment.addAccountingTransaction"
        >
          {{ t('addAccountingTransaction') }}
        </el-checkbox>
      </el-form-item>
    </input-section>

    <input-section>
      <h1>{{ t('sections.inventory') }}</h1>

      <!-- Loading state -->
      <div v-if="loadingAllItems" class="flex items-center justify-center py-10">
        <el-icon class="is-loading text-4xl"><Loading /></el-icon>
        <span class="ml-2">{{ t('loadingItems') }} ({{ loadingProgress }}%)...</span>
      </div>

      <!-- Search filter for large lists -->
      <div v-else-if="!inventoryAdjustment.empty()" class="mb-4">
        <div class="flex items-center justify-between gap-4">
          <div class="text-sm text-gray-600">
            {{ t('totalItems') }}: {{ inventoryAdjustment.inventoryAdjustmentItems.length }}
            <span v-if="filterQuery || showOnlyChanged"> | {{ t('filtered') }}: {{ filteredItems.length }}</span>
            <span class="text-green-600 ml-2" v-if="changedCount > 0">| {{ t('changed') }}: {{ changedCount }}</span>
          </div>
          <div class="flex items-center gap-4">
            <el-checkbox v-model="showOnlyChanged" :label="t('showOnlyChanged')" />
            <el-input
              v-model="filterQuery"
              :placeholder="t('searchItems')"
              clearable
              class="w-64"
              :prefix-icon="Search"
            />
          </div>
        </div>
      </div>
    </input-section>

    <div class="w-10/12 mt-8" v-if="!loadingAllItems && !inventoryAdjustment.empty()">
      <el-table
        :data="paginatedItems"
        style="width: 100%"
        :max-height="500"
        :row-class-name="tableRowClassName"
      >
        <el-table-column prop="item.name"
                         width="200"
                         :label="t('itemName')"></el-table-column>

        <el-table-column prop="oldQuantity" :label="t('oldQuantity')"
                         width="100" align="center">
          <template #default="props">
            <div class="el-input el-input--small">
              {{ computedQuantity(props.row) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="newQuantity" :label="t('newQuantity')"
                         width="200" align="center">
          <template #default="props">
            <el-input-number
                v-model="props.row.newQuantity"
                :step="1"
                size="small"
                controls-position="right"
            ></el-input-number>
          </template>
        </el-table-column>
        <el-table-column prop="item.barcode"
                         width="200"
                         :label="t('barcode')"></el-table-column>
        <el-table-column prop="extraProperties.batchNumber" :label="t('batchNumber')"
                         width="200" align="center">
          <template #default="props">
            <el-select v-if="props.row.item.isExpirable" v-model="props.row.extraProperties.batchNumber" :placeholder="t('batchNumber')">
              <template v-for="stock in props.row.item.itemStocks" :key="stock.extraProperties?.batchNumber">
                <el-option v-if="stock.extraProperties?.batchNumber" :label="stock.extraProperties?.batchNumber" :value="stock.extraProperties?.batchNumber" />
              </template>
            </el-select>
          </template>
        </el-table-column>


        <el-table-column
            prop="inventoryAdjustmentItemSerials"
            :label="t('serialNumbers')"
            width="300"
        >
          <template #default="props">
            <template v-if="props.row.item.isSerialized">
              <!-- add new serials -->
              <before-create
                  v-if="props.row.newQuantity > computedQuantity(props.row)"
                  :do="() => props.row.resetInventoryAdjustmentItemSerials()"
              >
                <tag-input
                    :title="
                    t('insertSerials', {
                      num: props.row.newQuantity - computedQuantity(props.row)
                    })
                  "
                    v-model="props.row.inventoryAdjustmentItemSerials"
                    :insertFunction="(val: any) => props.row.insertSerialNumber(val)"
                    :removeFunction="(index: any) => props.row.removeSerialNumber(index)"
                    :valueFormatter="(serialObject: any) => serialObject.itemSerial.number"
                    :validate="() => props.row.validateSerials()"
                />
              </before-create>

              <!-- remove old serials -->
              <div class="input-label"
                   v-else-if="props.row.newQuantity < computedQuantity(props.row)">
                {{
                  t('removeSerials', {
                    num: computedQuantity(props.row) - props.row.newQuantity
                  })
                }}
                <el-select
                    filterable
                    clearable
                    multiple
                    class="w-full"
                    v-model="props.row.toRemoveSerialIDs"
                    :multiple-limit="computedQuantity(props.row) - props.row.newQuantity"
                >
                  <el-option
                      v-for="(item, i) in props.row.item.itemStocks.find((stock: any) => stock.extraProperties?.batchNumber === props.row.extraProperties.batchNumber)?.itemSerials"
                      :key="i"
                      :label="item.number"
                      :value="item.id"
                  >
                    <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                      {{ item.number }}
                    </span>
                  </el-option>
                </el-select>
              </div>
            </template>
          </template>
        </el-table-column>

        <el-table-column width="50" align="center">
          <template #default="scope">
            <span class="text-red-600 cursor-pointer"
                  @click="removeItem(scope.$index)">
              <font-awesome-icon icon="trash"/>
            </span>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination for large datasets -->
      <div v-if="filteredItems.length > pageSize" class="flex justify-center items-center gap-4 mt-4">
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="filteredItems.length"
          :page-sizes="[50, 100, 200, 500]"
          layout="sizes, prev, pager, next, jumper"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>

    <!-- Cashier PIN Dialog -->
    <el-dialog v-model="cashierPinDialogOpen" width="30%" :title="t('enterCashierPin')">
      <div>
        <!-- Enter the pin -->
        <div class="my-2">
          <p>{{ t('enterCashierPin') }}</p>
        </div>

        <!-- password input up to 4 chars -->
        <div class="my-2">
          <el-input
            id="password"
            v-model="cashierPin"
            type="password"
            required
            show-password
            autofocus
            dir="ltr"
            :maxlength="4"
            :minlength="4"
            @keyup.enter="submitWithPin"
            ref="cashierPinInput"
          />
        </div>

        <!-- submit button -->
        <div class="flex items-center justify-between mb-2">
          <el-button
            type="primary"
            @click="submitWithPin"
            class="w-full"
          >
            {{ t('submitAdjustment') }}
          </el-button>
        </div>
      </div>
    </el-dialog>
  </el-form>
</template>

<script lang="ts">
import {defineComponent, nextTick} from 'vue'
import InventoryAdjustment, {
  InventoryAdjustmentItem,
  inventoryAdjustmentI18nMessages
} from '@/models/inventory/InventoryAdjustment'
import {ElForm} from 'element-plus'
import { Search, Loading } from '@element-plus/icons-vue'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import TagInput from '@/components/form/TagInput.vue'
import Item from '@/models/inventory/Item'
import {useI18n} from 'vue-i18n'
import { useSettingsStore } from '@/stores/settingsStore'

export default defineComponent({
  name: 'bulk-inventory-adjustment',

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...inventoryAdjustmentI18nMessages.en,
          itemName: 'Item Name',

          sections: {
            info: 'Meta data',
            inventory: 'Inventory Info'
          },

          placeholders: {
            reason: 'Example: missing a couple of items',
            itemName: 'Enter barcode, sku, or item name'
          },

          userAdjusting: 'Employee responsible for this inventory adjustment',
          insertSerials: 'You must insert {num} new serial numbers',
          removeSerials: 'You must remove {num} old serial numbers',

          successMsg: 'Inventory was adjusted successfully',
          add: 'Add',
          batchNumber: 'Batch Number',
          enterCashierPin: 'Enter Cashier PIN',
          errCashierPinRequired: 'Cashier PIN is required',
          submitAdjustment: 'Submit Adjustment',
          loadingItems: 'Loading items',
          searchItems: 'Search by name or barcode...',
          totalItems: 'Total items',
          filtered: 'Filtered',
          changed: 'Changed',
          showOnlyChanged: 'Show only changed',
          barcode: 'Barcode'
        },
        ar: {
          ...inventoryAdjustmentI18nMessages.ar,
          itemName: 'اسم المادة',

          sections: {
            info: 'البيانات الوصفية',
            inventory: 'البضاعة المعدلة'
          },

          placeholders: {
            reason: 'مثلا: بعض البضاعة مفقودة',
            itemName: 'ادخل اسم المنتج او الباركود او SKU'
          },

          userAdjusting: 'الموظف المسؤول عن تعديل البضاعة',
          insertSerials: 'قم بادخال {num} serial numbers',
          removeSerials: 'قم بمسح {num} serial numbers',

          successMsg: 'تم تعديل البضاعة بنجاح',
          add: 'اضافة',
          batchNumber: 'رقم الوجبة',
          enterCashierPin: 'أدخل رقم الموظف',
          errCashierPinRequired: 'رقم الموظف مطلوب',
          submitAdjustment: 'إرسال التعديل',
          loadingItems: 'جاري تحميل المواد',
          searchItems: 'البحث بالاسم أو الباركود...',
          totalItems: 'إجمالي المواد',
          filtered: 'مفلترة',
          changed: 'المتغيرة',
          showOnlyChanged: 'عرض المتغيرة فقط',
          barcode: 'Barcode'
        }
      }
    })

    return {t, Search, Loading}
  },

  components: {
    AlertBadge,
    HorizontalButton,
    TagInput,
    InputSection
  },

  data() {
    return {
      inventoryAdjustment: new InventoryAdjustment(),
      error: {title: '', body: ''},
      user: this.$ctx.currentUser,
      rules: {
        reason: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ]
      },
      cashierPinDialogOpen: false,
      cashierPin: '',
      // Bulk loading
      loadingAllItems: false,
      loadingProgress: 0,
      // Filtering and pagination
      filterQuery: '',
      showOnlyChanged: false,
      currentPage: 1,
      pageSize: 50,
      // Track original quantities to detect changes
      originalQuantities: new Map<string, number>(),
    }
  },

  computed: {
    filteredItems(): InventoryAdjustmentItem[] {
      let items = this.inventoryAdjustment.inventoryAdjustmentItems

      if (this.showOnlyChanged) {
        items = items.filter(item => this.hasItemChanged(item))
      }

      if (this.filterQuery) {
        const query = this.filterQuery.toLowerCase()
        items = items.filter(item =>
          item.item.name.toLowerCase().includes(query) ||
          (item.item.barcode && item.item.barcode.toLowerCase().includes(query)) ||
          (item.item.sku && item.item.sku.toLowerCase().includes(query))
        )
      }

      return items
    },

    paginatedItems(): InventoryAdjustmentItem[] {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredItems.slice(start, end)
    },

    changedCount(): number {
      return this.inventoryAdjustment.inventoryAdjustmentItems.filter(
        item => this.hasItemChanged(item)
      ).length
    },
  },

  watch: {
    filterQuery() {
      this.currentPage = 1
    },
    showOnlyChanged() {
      this.currentPage = 1
    }
  },

  mounted() {
    // Auto-load all items when page loads
    this.loadAllItems()
  },

  methods: {
    computedQuantity(row: any) {
      // For expirable items, require batch number selection first
      if (row.item.isExpirable) {
        if (row.extraProperties.batchNumber) {
          return row.item.itemStocks.find((stock: any) => stock.extraProperties?.batchNumber === row.extraProperties.batchNumber)?.quantity || 0
        }
        return 0 // Don't show quantity until batch is selected
      }
      return row.oldQuantity
    },

    handlePageChange(page: number) {
      this.currentPage = page
    },

    handleSizeChange(size: number) {
      this.pageSize = size
      this.currentPage = 1
    },

    tableRowClassName({ row }: { row: InventoryAdjustmentItem }) {
      if (this.hasItemChanged(row)) {
        return 'changed-row'
      }
      return ''
    },

    async loadAllItems() {
      this.loadingAllItems = true
      this.loadingProgress = 0
      this.inventoryAdjustment = new InventoryAdjustment()

      try {
        const url = `${Item.ENDPOINT}?type=allItems&q=`
        const items = await this.$http.get<Item[]>(url)

        // Filter out unlimited stock items
        const filteredItems = items.filter(item => !item.unlimitedStock)

        // Process items in chunks to avoid UI blocking
        const chunkSize = 50
        const totalItems = filteredItems.length

        for (let i = 0; i < totalItems; i += chunkSize) {
          const chunk = filteredItems.slice(i, i + chunkSize)

          for (const itemData of chunk) {
            // Use the same addItem logic as NewInventoryAdjustment
            this.inventoryAdjustment.addItem(itemData)
            // Track original quantity
            const addedItem = this.inventoryAdjustment.inventoryAdjustmentItems[
              this.inventoryAdjustment.inventoryAdjustmentItems.length - 1
            ]
            const key = `${addedItem.item.id}`
            this.originalQuantities.set(key, addedItem.oldQuantity)
          }

          // Update progress
          this.loadingProgress = Math.round(((i + chunk.length) / totalItems) * 100)

          // Yield to browser to keep UI responsive
          if (i + chunkSize < totalItems) {
            await nextTick()
          }
        }

        this.$alertModal.showSuccess({
          title: `Loaded ${this.inventoryAdjustment.inventoryAdjustmentItems.length} items`
        })
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title || 'Failed to load items' })
      } finally {
        this.loadingAllItems = false
        this.loadingProgress = 100
      }
    },

    removeItem(index: number) {
      // Find the actual index in the main array when using pagination
      const item = this.paginatedItems[index]
      const actualIndex = this.inventoryAdjustment.inventoryAdjustmentItems.indexOf(item)
      if (actualIndex !== -1) {
        this.inventoryAdjustment.removeItem(actualIndex)
      }
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
      this.inventoryAdjustment = new InventoryAdjustment()
      this.originalQuantities.clear()
      this.filterQuery = ''
      this.currentPage = 1
    },

    hasItemChanged(item: InventoryAdjustmentItem): boolean {
      const key = `${item.item.id}`
      const originalQty = this.originalQuantities.get(key) ?? item.oldQuantity
      return item.newQuantity !== originalQty
    },

    getChangedItems(): InventoryAdjustmentItem[] {
      return this.inventoryAdjustment.inventoryAdjustmentItems.filter(
        item => this.hasItemChanged(item)
      )
    },

    async submit(ir: InventoryAdjustment, pin: string = '') {
      this.error.title = ''
      this.error.body = ''

      // Only submit changed items
      const changedItems = this.getChangedItems()
      if (changedItems.length === 0) {
        this.$alertModal.showDanger({ title: 'No changes to submit' })
        return
      }

      // Create new adjustment with only changed items
      const adjustmentToSubmit = new InventoryAdjustment()
      adjustmentToSubmit.reason = ir.reason
      adjustmentToSubmit.addAccountingTransaction = ir.addAccountingTransaction
      adjustmentToSubmit.inventoryAdjustmentItems = changedItems

      try {
        const headers = pin ? { pin: pin } : {}
        await this.$http.post(InventoryAdjustment.ENDPOINT, adjustmentToSubmit, { headers })
        await this.$router.push(this.$Route.INVENTORY_INVENTORY_ADJUSTMENTS)
      } catch (error: any) {
        return error
      }
    },

    checkForm() {
      const errors = []
      // Only validate changed items
      const changedItems = this.getChangedItems()
      for (const iai of changedItems) {
        errors.push(iai.validateSerials())
      }

      this.error.body = errors.filter((err) => err).join('\n')

      if (this.error.body) {
        this.error.title = this.$t('validation.inputErrors')
        document.getElementById('main')?.scrollTo({top: 0})
        return false
      }

      return true
    },

    async validateSubmit(formName: string) {
      this.inventoryAdjustment.preparePayloadData()
      const form = this.$refs[formName] as typeof ElForm

      form?.validate(async (valid: boolean) => {
        if (!valid || !this.checkForm()) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        }

        // Check if PIN is required
        if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
          this.cashierPinDialogOpen = true
          this.$nextTick(() => {
            setTimeout(() => {
              this.getCashierInputRef().focus()
            }, 0)
          })
          return
        }

        // Submit without PIN
        const resp = await this.submit(this.inventoryAdjustment as InventoryAdjustment)

        if (resp) {
          this.error.title = (resp as any)?.title
          this.error.body = (resp as any)?.body
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        } else {
          this.clearForm(formName)
          this.$alertModal.showSuccess({title: this.t('successMsg')})
        }
        return true
      })
    },

    async submitWithPin() {
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (this.cashierPin === '') {
          this.$alertModal.showDanger({
            title: this.t('errCashierPinRequired')
          })
          return
        }
      }

      this.cashierPinDialogOpen = false
      const resp = await this.submit(this.inventoryAdjustment as InventoryAdjustment, this.cashierPin)

      if (resp) {
        this.error.title = (resp as any)?.title || 'Error'
        this.error.body = (resp as any)?.body || 'An error occurred'
        document.getElementById('main')?.scrollTo({ top: 0 })
      } else {
        this.clearForm('inventoryAdjustmentForm')
        this.$alertModal.showSuccess({ title: this.t('successMsg') })
      }

      // Clear PIN for security
      this.cashierPin = ''
    },

    getCashierInputRef() {
      return (this.$refs['cashierPinInput'] as any)
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply p-2 text-lg py-3 font-bold;
}

form > h2 {
  @apply p-2 py-3 font-bold;
}

form > h3 {
  @apply p-2 py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>

<style>
.el-table .changed-row {
  background-color: #f0fdf4 !important;
}

.el-table .changed-row:hover > td {
  background-color: #dcfce7 !important;
}
</style>
